import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Button,
  Divider,
  Chip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import RecyclingRoundedIcon from '@mui/icons-material/RecyclingRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import { getUser, clearAuth } from '../../lib/auth';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import adminNavbarI18n from '../../i18n/components/admin/navbar.i18n';

export default function AdminNavbar() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const user = getUser();
  const isMobile = useMediaQuery('(max-width:600px)');

  // i18n
  const { t, register } = useI18n();
  React.useEffect(() => { register('adminNavbar', adminNavbarI18n); }, [register]);

  // liens (Users + Logs)
  const links = React.useMemo(() => ([
    { to: '/admin/products',     label: t('adminNavbar.links.products'),     icon: <Inventory2OutlinedIcon fontSize="small" /> },
    { to: '/admin/transactions', label: t('adminNavbar.links.transactions'), icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
    { to: '/admin/users',        label: t('adminNavbar.links.users'),        icon: <GroupOutlinedIcon fontSize="small" /> },
    { to: '/admin/prospects',    label: 'Prospects',                          icon: <CampaignOutlinedIcon fontSize="small" /> },
    { to: '/admin/collect',      label: 'Collect',                            icon: <RecyclingRoundedIcon fontSize="small" /> },
    { to: '/admin/logs',         label: t('adminNavbar.links.logs'),         icon: <ListAltOutlinedIcon fontSize="small" /> },
  ]), [t]);

  const logout = () => {
    clearAuth();
    nav('/login', { replace: true });
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: '#ffffff',
        color: 'text.primary',
        border: '1px solid #eee',
        borderRadius: 2,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 56, gap: 1 }}>
          {/* Liens */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              overflowX: { xs: 'auto', sm: 'visible' },
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              px: { xs: 0.5, sm: 0 },
            }}
          >
            {links.map(link => {
              const active = pathname.startsWith(link.to);
              return (
                <Button
                  key={link.to}
                  component={NavLink}
                  to={link.to}
                  startIcon={link.icon}
                  disableRipple
                  sx={{
                    px: { xs: 1.25, sm: 1.5 },
                    py: { xs: 0.6, sm: 0.75 },
                    minWidth: { xs: 'auto', sm: 64 },
                    textTransform: 'none',
                    fontWeight: active ? 800 : 600,
                    fontSize: { xs: 14, sm: 15 },
                    color: active ? '#0466c8' : 'text.primary',
                    bgcolor: active ? 'rgba(11,95,255,0.08)' : 'transparent',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: { xs: 0.5, sm: 0.75 } },
                    '&:hover': { bgcolor: 'rgba(11,95,255,0.12)' },
                    position: 'relative',
                    '&::after': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: 8,
                          right: 8,
                          bottom: { xs: -7, sm: -9 },
                          height: 3,
                          borderRadius: 3,
                          backgroundColor: '#0466c8',
                        }
                      : {},
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Rôle + déconnexion */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!isMobile && (
              <Chip
                label={user?.role || '—'}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: 'rgba(11,95,255,0.08)',
                  color: '#0466c8',
                }}
              />
            )}
            {isMobile ? (
              <IconButton
                onClick={logout}
                aria-label={t('adminNavbar.logout')}
                sx={{ color: '#0466c8' }}
              >
                <LogoutIcon />
              </IconButton>
            ) : (
              <Button
                onClick={logout}
                startIcon={<LogoutIcon />}
                variant="text"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#0466c8',
                  '&:hover': { backgroundColor: 'rgba(11,95,255,0.08)' },
                }}
              >
                {t('adminNavbar.logout')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
      <Divider />
    </AppBar>
  );
}
